import { Alert, Badge, Card, Group, Progress, RingProgress, SimpleGrid, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconMail, IconShieldLock, IconUserX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { STATUS, fmtDateTime } from '../../lib/format';
import { useMe } from '../../lib/me';
import type { AssignmentStatus } from '../../lib/types';
import { PageHeader } from '../../ui/PageHeader';
import { QueryState } from '../../ui/QueryState';

interface Group { name: string; total: number; completed: number; onTime: number; overdue: number }
interface Summary { assigned: number; metricOnTimeShare: number | null; onTime: number; byStatus: Record<AssignmentStatus, number>; byDepartment: Group[]; byCourse: Group[] }
interface System { retention: { at: string; assignments: number; users: number; checkOk: boolean } | null; reminders: string | null; mailErrors24h: number; mailSent24h: number; authDenied24h: number }

const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);

function Shares({ title, rows }: { title: string; rows: Group[] }) {
  return (
    <Card withBorder radius="lg" padding="lg">
      <Title order={5} mb="md">{title}</Title>
      <Stack gap="md">
        {rows.map((r) => (
          <div key={r.name}>
            <Group justify="space-between" mb={4} wrap="nowrap">
              <Text size="sm" fw={500} lineClamp={1}>{r.name}</Text>
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>в срок {pct(r.onTime, r.total)}% · завершили {pct(r.completed, r.total)}%</Text>
            </Group>
            <Progress.Root size="lg" radius="xl">
              <Progress.Section value={pct(r.onTime, r.total)} color="teal" />
              <Progress.Section value={pct(r.completed - r.onTime, r.total)} color="yellow" />
              <Progress.Section value={pct(r.overdue, r.total)} color="red" />
            </Progress.Root>
          </div>
        ))}
        {rows.length === 0 && <Text size="sm" c="dimmed">Назначений нет</Text>}
      </Stack>
    </Card>
  );
}

export function DashboardPage() {
  const me = useMe();
  const canSummary = me.roles.includes('RESULTS') || me.roles.includes('ASSIGNMENTS');
  const s = useQuery({ queryKey: ['summary'], queryFn: () => api.get<Summary>('/admin/summary'), enabled: canSummary });
  const sys = useQuery({ queryKey: ['system'], queryFn: () => api.get<System>('/admin/system') });
  return (
    <>
      <PageHeader title="Сводка" subtitle="Без ФИО: доли по подразделениям и курсам. Метрика успеха - доля завершивших к исходному сроку; перенос срока её не улучшает." />
      <Stack gap="lg">
        {canSummary && (
          <QueryState loading={s.isLoading} error={s.error}>
            {() => {
              const d = s.data!;
              const share = d.metricOnTimeShare == null ? 0 : Math.round(d.metricOnTimeShare * 100);
              return (
                <>
                  <SimpleGrid cols={{ base: 1, md: 3 }}>
                    <Card withBorder radius="lg" padding="lg">
                      <Group wrap="nowrap">
                        <RingProgress size={110} thickness={11} roundCaps sections={[{ value: share, color: 'teal' }]} label={<Text ta="center" fw={800}>{share}%</Text>} />
                        <div>
                          <Text fw={600}>Завершили к исходному сроку</Text>
                          <Text size="sm" c="dimmed">{d.onTime} из {d.assigned} назначений (без отменённых)</Text>
                          <Badge mt={6} variant="outline" color="gray">цель X% ждёт подтверждения</Badge>
                        </div>
                      </Group>
                    </Card>
                    <Card withBorder radius="lg" padding="lg" style={{ gridColumn: 'span 2' }}>
                      <Text fw={600} mb="sm">По статусам</Text>
                      <Group gap="xs">
                        {(Object.keys(STATUS) as AssignmentStatus[]).map((k) => (
                          <Badge key={k} size="lg" color={STATUS[k].color} variant="light">{STATUS[k].label}: {d.byStatus[k]}</Badge>
                        ))}
                      </Group>
                    </Card>
                  </SimpleGrid>
                  <SimpleGrid cols={{ base: 1, md: 2 }}>
                    <Shares title="Подразделения" rows={d.byDepartment} />
                    <Shares title="Курсы" rows={d.byCourse} />
                  </SimpleGrid>
                  <Group gap="md">
                    <Group gap={4}><Badge color="teal" circle> </Badge><Text size="xs" c="dimmed">в срок</Text></Group>
                    <Group gap={4}><Badge color="yellow" circle> </Badge><Text size="xs" c="dimmed">с опозданием</Text></Group>
                    <Group gap={4}><Badge color="red" circle> </Badge><Text size="xs" c="dimmed">просрочено или заблокировано</Text></Group>
                  </Group>
                </>
              );
            }}
          </QueryState>
        )}
        {!canSummary && <Alert color="gray">У роли «Контент» нет доступа к результатам сотрудников. Работа с курсами - в разделах «Контент».</Alert>}
        <QueryState loading={sys.isLoading} error={sys.error}>
          {() => {
            const y = sys.data!;
            const item = (icon: React.ReactNode, color: string, label: string, value: string) => (
              <Card withBorder radius="lg" padding="md">
                <Group wrap="nowrap"><ThemeIcon size={36} radius="md" variant="light" color={color}>{icon}</ThemeIcon><div><Text size="xs" c="dimmed">{label}</Text><Text size="sm" fw={600}>{value}</Text></div></Group>
              </Card>
            );
            return (
              <div>
                <Title order={5} mb="sm">Состояние системы</Title>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
                  {item(y.retention?.checkOk === false ? <IconAlertTriangle size={18} /> : <IconCheck size={18} />, y.retention?.checkOk === false ? 'red' : 'teal', 'Обезличивание по сроку', y.retention ? `${fmtDateTime(y.retention.at)} · ${y.retention.assignments} назн., проверка ${y.retention.checkOk ? 'ок' : 'НЕ ПРОЙДЕНА'}` : 'ещё не запускалось')}
                  {item(<IconMail size={18} />, y.mailErrors24h ? 'red' : 'blue', 'Письма за 24 часа', `отправлено ${y.mailSent24h}, ошибок ${y.mailErrors24h}`)}
                  {item(<IconShieldLock size={18} />, 'blue', 'Напоминания', y.reminders ? `проверка ${fmtDateTime(y.reminders)}` : 'ещё не запускались')}
                  {item(<IconUserX size={18} />, y.authDenied24h ? 'orange' : 'gray', 'Отказы во входе за 24 часа', String(y.authDenied24h))}
                </SimpleGrid>
              </div>
            );
          }}
        </QueryState>
      </Stack>
    </>
  );
}
